/**
 * Where the access token lives.
 *
 * <h2>This module exists to be replaced</h2>
 *
 * The backend already supports the safer arrangement: send `X-Auth-Cookie: true`
 * on the token request and it puts the tokens in `HttpOnly`, `Secure`,
 * `SameSite=Lax` cookies that no script can read. That is the intended end state.
 *
 * <p>Until then the token is held here and sent as a bearer header, which is a
 * deliberate, temporary trade with a real cost: <b>a token readable by JavaScript
 * is a token any cross-site scripting flaw can take</b>. Nothing else in this
 * application knows where the token is kept, so making the switch is a change to
 * this file and to the one interceptor that reads it — not a search across
 * features.
 *
 * <h2>Why storage rather than a variable</h2>
 *
 * A token in a module variable is gone on every page reload, which reads as being
 * signed out at random. Given the token is already reachable from script, moving
 * it out of storage buys nothing against the threat that matters and costs the
 * session on every refresh.
 *
 * <h2>One store, and no "Keep me signed in"</h2>
 *
 * `localStorage`, always. The checkbox and the `sessionStorage` half went on 28 September
 * 2026: signing in is something somebody does once and expects to hold, and the products
 * this one is measured against — Slack, Notion, Linear, Stripe — do not ask the question at
 * all. They keep you signed in and put the control where it belongs, on a sign-out that
 * reaches every device.
 *
 * <p>The unticked case was not neutral, either. It put the session in `sessionStorage`,
 * which is scoped to **one tab**, so opening a link from an email in a second tab arrived
 * signed out — with no way to tell that apart from a bug, which is exactly how it was
 * reported.
 *
 * <p>It came back for part of 3 October 2026 with the design ported from the previous product's React app, and
 * went again the same day once the cost was weighed against the design. That round trip is
 * the reason this section is longer than it needs to be: the next person to port a sign-in
 * screen will find the checkbox in the source too.
 *
 * <p>`clear()` still empties both stores. Nothing writes to `sessionStorage` any more, but a
 * browser that signed in before this change is still holding tokens there, and a stale
 * credential nothing reads is still a credential.
 */

const ACCESS_TOKEN = 'xtremand.access_token';
const REFRESH_TOKEN = 'xtremand.refresh_token';

/**
 * Where a session is kept, and the only reason `session` is still named.
 *
 * <p>Nothing writes to it. It exists so `clear()` can sweep what earlier versions
 * of this file left there — see the note above.
 */
type Store = 'local' | 'session';

function storage(store: Store): Storage {
  return store === 'local' ? window.localStorage : window.sessionStorage;
}

/**
 * Reads a key, tolerating storage being unavailable.
 *
 * <p>`localStorage` throws rather than returning null in a private window with
 * site data blocked, and in some embedded webviews. An unreadable token means
 * signed out, which is a state this application already handles — an exception
 * escaping here would blank the page instead.
 */
function readFrom(store: Store, key: string): string | null {
  try {
    return storage(store).getItem(key);
  } catch {
    return null;
  }
}

function writeTo(store: Store, key: string, value: string): void {
  try {
    storage(store).setItem(key, value);
  } catch {
    // Nothing to do and nobody to tell: the session lasts until reload, which is
    // strictly better than refusing to sign in.
  }
}

function removeFrom(store: Store, key: string): void {
  try {
    storage(store).removeItem(key);
  } catch {
    // See writeTo().
  }
}

export const tokenStorage = {
  // `localStorage` only. Reading `sessionStorage` as well would resurrect a session an
  // older version of this application left there -- nothing writes it now, so anything
  // found would be stale.
  accessToken: (): string | null => readFrom('local', ACCESS_TOKEN),

  refreshToken: (): string | null => readFrom('local', REFRESH_TOKEN),

  /**
   * Begins a session.
   *
   * <p>Everything held before is cleared first, including anything an older
   * version of this application left in `sessionStorage`. Signing in as one
   * person over another's leftovers is how a token outlives the session it
   * belonged to.
   *
   * <p>It takes no options. It took `remember` for part of 3 October 2026, with the
   * checkbox the ported design carries, and both went the same day: choosing the store was
   * the whole of that feature, and the store it chose was the fault.
   */
  start(tokens: { accessToken: string; refreshToken?: string | undefined }): void {
    tokenStorage.clear();

    writeTo('local', ACCESS_TOKEN, tokens.accessToken);
    if (tokens.refreshToken) {
      writeTo('local', REFRESH_TOKEN, tokens.refreshToken);
    }
  },

  /**
   * Replaces the tokens of a session that already exists.
   *
   * <p>This is token rotation, not a sign-in: the refresh endpoint issues a new
   * pair every time because the client is configured `reuse-refresh-tokens:
   * false`. Distinct from `start` because it must NOT clear first — a refresh
   * that emptied storage between reading the old token and writing the new one
   * would sign somebody out if it failed in between.
   *
   * <p>With no session to rotate there is nothing to write. A token landing where
   * nothing is reading is a session nobody can use.
   */
  save(tokens: { accessToken: string; refreshToken?: string | undefined }): void {
    if (readFrom('local', ACCESS_TOKEN) === null) return;

    writeTo('local', ACCESS_TOKEN, tokens.accessToken);
    if (tokens.refreshToken) {
      writeTo('local', REFRESH_TOKEN, tokens.refreshToken);
    }
  },

  /**
   * Forgets both tokens.
   *
   * <p>Called on sign-out and on a 401 that could not be refreshed. It does not
   * revoke anything server-side — that is what the logout endpoint is for, and a
   * client that only forgets a token leaves it valid until it expires.
   */
  clear(): void {
    // Both stores. Only one is written to; the other may still hold a session from before
    // this application stopped using it, and signing out has to reach that too.
    for (const store of ['local', 'session'] as const) {
      removeFrom(store, ACCESS_TOKEN);
      removeFrom(store, REFRESH_TOKEN);
    }
  },
};
